import React from 'react';
import { MovieScene, MovieVideoModel } from '../../services/api/movieService';
import { getVideoModelSpec } from '../video-generation/videoModelCatalog';
import { Video, RefreshCw, Loader2, AlertCircle, Lock, Sparkles } from 'lucide-react';

/**
 * ステップ③: クリップ生成。
 *
 * 採用済みの絵コンテを開始フレームとして、シーンごとに動画クリップを生成する。
 * 絵コンテ未生成のシーンはロック表示になる。
 */

interface ClipStepProps {
  scenes: MovieScene[];
  aspectRatio: string;
  videoModel: MovieVideoModel;
  generatingSceneIds: Set<string>;
  onGenerate: (sceneId: string) => void;
  onGenerateAll: () => void;
}

const aspectClass = (ratio: string): string => {
  switch (ratio) {
    case '9:16':
      return 'aspect-[9/16]';
    case '1:1':
      return 'aspect-square';
    default:
      return 'aspect-video';
  }
};

const PROGRESS_STEP_LABELS: Record<string, string> = {
  initializing: '初期化中',
  optimizing_prompt: '準備中',
  generating: '動画を生成中',
  downloading: 'ダウンロード中',
  uploading: 'アップロード中',
  saving: '保存中',
};

const ClipStep: React.FC<ClipStepProps> = ({
  scenes,
  aspectRatio,
  videoModel,
  generatingSceneIds,
  onGenerate,
  onGenerateAll,
}) => {
  if (scenes.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-300 rounded-xl">
        先に「① 台本・構成」でシーンを作成してください
      </div>
    );
  }

  const readyScenes = scenes.filter(
    (s) =>
      s.storyboard.image &&
      (s.clip.status === 'none' || s.clip.status === 'failed')
  );
  const activeCount = scenes.filter(
    (s) => s.clip.status === 'pending' || s.clip.status === 'processing'
  ).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">
          絵コンテをもとに各シーンを動画化します。使用モデル: {getVideoModelSpec(videoModel).label}
        </p>
        <button
          onClick={onGenerateAll}
          disabled={readyScenes.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50 shrink-0 ml-4"
        >
          <Sparkles className="w-4 h-4" />
          未生成を一括生成（{readyScenes.length}件）
        </button>
      </div>

      {activeCount > 0 && (
        <div className="mb-4 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          {activeCount}件のクリップを生成中です。完了すると自動で表示されます。
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {scenes.map((scene, index) => {
          const status = scene.clip.status;
          const isRequesting = generatingSceneIds.has(scene.id);
          const isActive = status === 'pending' || status === 'processing' || isRequesting;
          const isLocked = !scene.storyboard.image;

          return (
            <div key={scene.id} className="border border-warm-border rounded-xl overflow-hidden bg-white">
              <div className={`${aspectClass(aspectRatio)} bg-gray-100 relative flex items-center justify-center`}>
                {isLocked ? (
                  <div className="flex flex-col items-center text-gray-400 px-4 text-center">
                    <Lock className="w-7 h-7 mb-2" />
                    <span className="text-xs">先に「② 絵コンテ」を生成してください</span>
                  </div>
                ) : scene.clip.video ? (
                  <video
                    src={scene.clip.video.url}
                    controls
                    preload="metadata"
                    className="w-full h-full object-cover"
                  />
                ) : isActive ? (
                  <div className="flex flex-col items-center text-gray-400 w-full px-6">
                    <Loader2 className="w-7 h-7 animate-spin mb-2" />
                    <span className="text-xs mb-2">
                      {PROGRESS_STEP_LABELS[scene.clip.progress_step ?? ''] ?? '生成中'}...
                    </span>
                    {scene.clip.progress_percentage != null && (
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-primary h-1.5 rounded-full transition-all"
                          style={{ width: `${scene.clip.progress_percentage}%` }}
                        />
                      </div>
                    )}
                  </div>
                ) : status === 'failed' ? (
                  <div className="flex flex-col items-center text-red-400 px-4 text-center">
                    <AlertCircle className="w-7 h-7 mb-2" />
                    <span className="text-xs line-clamp-3">{scene.clip.error_message ?? '生成に失敗しました'}</span>
                  </div>
                ) : (
                  // 絵コンテをプレビューとして薄く表示し、動画未生成であることを示す
                  <>
                    <img
                      src={scene.storyboard.image!.url}
                      alt=""
                      className="w-full h-full object-cover opacity-40"
                    />
                    <Video className="w-9 h-9 text-gray-500 absolute" />
                  </>
                )}
              </div>

              <div className="p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-gray-700">
                    シーン {index + 1}
                    <span className="ml-1.5 font-normal text-gray-400">{scene.duration_sec}秒</span>
                  </span>
                  <button
                    onClick={() => onGenerate(scene.id)}
                    disabled={isLocked || isActive || !scene.clip_prompt}
                    className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                    title={
                      isLocked
                        ? '絵コンテが必要です'
                        : !scene.clip_prompt
                          ? '①でクリッププロンプトを入力してください'
                          : undefined
                    }
                  >
                    <RefreshCw className={`w-3 h-3 ${isRequesting ? 'animate-spin' : ''}`} />
                    {scene.clip.video || status === 'failed' ? '再生成' : '生成'}
                  </button>
                </div>
                {scene.summary && (
                  <p className="text-xs text-gray-600 line-clamp-2">{scene.summary}</p>
                )}
                {scene.clip_prompt && (
                  <details className="mt-1.5">
                    <summary className="text-[11px] text-gray-400 cursor-pointer select-none">プロンプト</summary>
                    <p className="text-[11px] text-gray-500 mt-1 whitespace-pre-wrap">{scene.clip_prompt}</p>
                  </details>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ClipStep;
